import { useCurrencyNumberFormatter } from 'hooks';
import useHasCostPermission from 'hooks/permission/useHasCostPermission';
import { Duration } from 'luxon';
import { convertDurationToHoursDecimal, secondsToHmmString } from 'utils/dateUtils';
import { t } from 'utils/localize';
import { IActivityReportRowInfo } from './ActivityReportData';
import useMultiEquipmentTrackingEnabled from 'hooks/settings/useMultiEquipmentTrackingEnabled/useMultiEquipmentTrackingEnabled';
import { ActivityReportType } from '../ActivityReportFilter/ActivityReportFilter';
import { remainingDataItemId } from 'utils/constants/utilConstants';

export default function useActivityReportExport(
  secondaryReportType?: ActivityReportType,
  filterReportType?: ActivityReportType,
  filterId?: string
) {
  const costFormatter = useCurrencyNumberFormatter('usd', { maximumFractionDigits: 2 })!;
  const canViewCost = useHasCostPermission();
  const hasMultiEquipmentTracking = useMultiEquipmentTrackingEnabled();
  const shouldIncludeEquipmentColumns =
    secondaryReportType === ActivityReportType.BY_EQUIPMENT ||
    (filterReportType === ActivityReportType.BY_EQUIPMENT && filterId !== remainingDataItemId);
  const includeAddOns = hasMultiEquipmentTracking && shouldIncludeEquipmentColumns;

  function getExportData<DataType extends IActivityReportRowInfo>(
    data: DataType[],
    customColumns: (row: DataType) => object
  ) {
    return data.map((row) => {
      const costData: any = {};
      if (canViewCost && shouldIncludeEquipmentColumns) {
        costData[t('Regular Cost (Labor)')] = costFormatter.format(row.regularCost);
        costData[t('Overtime Cost (Labor)')] = costFormatter.format(row.overtimeCost);
        costData[t('Double Time Cost (Labor)')] = costFormatter.format(row.doubletimeCost);
        costData[t('Labor Burden')] = costFormatter.format(row.laborBurden);
        costData[t('Total Cost (Labor)')] = costFormatter.format(row.totalCost);
        costData[t('Equip. Rate')] = costFormatter.format(row.equipmentRate);
        costData[t('Equip. Cost')] = costFormatter.format(row.equipmentCost);
        costData[t('Total Cost')] = costFormatter.format(row.equipmentTotalCost);
      } else if (canViewCost) {
        costData[t('Regular Cost')] = costFormatter.format(row.regularCost);
        costData[t('Overtime Cost')] = costFormatter.format(row.overtimeCost);
        costData[t('Double Time Cost')] = costFormatter.format(row.doubletimeCost);
        costData[t('Labor Burden')] = costFormatter.format(row.laborBurden);
        costData[t('Total Cost')] = costFormatter.format(row.totalCost);
      }

      const addOnData = {
        [t('Labor Hours')]: secondsToHmmString(row.totalHours),
        [t('Labor Hours (Decimal)')]: convertDurationToHoursDecimal(Duration.fromMillis(row.totalHoursDec * 1000), 2),
        [t('Add-On Hours')]: secondsToHmmString(row.addOnTime),
        [t('Add-On Hours (Decimal')]: convertDurationToHoursDecimal(Duration.fromMillis(row.addOnTime * 1000), 2),
      };

      const total = includeAddOns ? row.totalHours + row.addOnTime : row.totalHours;

      // If we decide to allow import/reimport we should get rid of the translation
      // due to having to untranslate on reimporting the file.
      return {
        ...customColumns(row),
        [t('Regular Time')]: secondsToHmmString(row.regularHours),
        [t('Regular Time (Decimal)')]: convertDurationToHoursDecimal(
          Duration.fromMillis(row.regularHoursDec * 1000),
          2
        ),
        [t('Overtime')]: secondsToHmmString(row.overtimeHours),
        [t('Overtime (Decimal)')]: convertDurationToHoursDecimal(Duration.fromMillis(row.overtimeHoursDec * 1000), 2),
        [t('Double Time')]: secondsToHmmString(row.doubleTimeHours),
        [t('Double Time (Decimal)')]: convertDurationToHoursDecimal(
          Duration.fromMillis(row.doubleTimeHoursDec * 1000),
          2
        ),
        ...(includeAddOns ? addOnData : {}),
        [t('Total')]: secondsToHmmString(total),
        [t('Total (Decimal)')]: convertDurationToHoursDecimal(Duration.fromMillis(total * 1000), 2),
        ...costData,
      };
    });
  }

  return getExportData;
}
